<script>
  import { Modal } from "svelte-utilities";
  import { createEventDispatcher } from "svelte";

  import PlusIcon from "@local/assets/icons/plus.svg";
  import XIcon from "@local/assets/icons/x.svg";
  import ImageInput from "#lib/sidebar/ImageInput.svelte";
  import ContenteditableInput from "#lib/sidebar/ContenteditableInput.svelte";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import CopyTextField from "#lib/sidebar/CopyTextField.svelte";
  import imageUrl from "#src/extensions/image-url.js";
  import BooleanInput from "#lib/sidebar/BooleanInput.svelte";
  import MultiSelectInput from "#lib/sidebar/MultiSelectInput.svelte";

  import { api } from "#src/api";
  import { profile } from "#src/stores/auth.js";

  export let product;
  export let disabled = false;
  export let itemProducts = [];

  const dispatch = createEventDispatcher();

  let tierModal;
  let newTier = {
    multiplier: 1,
    affected_products: [],
  };

  $: multiple = Array.isArray(product.id);

  function update(prop, value) {
    dispatch("update", { id: product.id, prop, value });
  }

  async function uploadImage(e) {
    if (e.detail.type && e.detail.data) {
      const uuid = crypto.randomUUID();
      const filename = `${uuid}/${e.detail.filename}`;
      const { data: result, error } = await api.storage.from("images").upload(filename, e.detail.data, {
        contentType: e.detail.contentType,
      });

      if (!error) {
        const image = {
          id: uuid,
          bucket: "images",
          object_id: result.path,
          content_type: e.detail.contentType,
          filename: e.detail.filename,
        };
        dispatch("update", { id: product.id, prop: "image", value: image });
      }
    }
  }

  function beginAddingTier() {
    newTier = {
      multiplier: 1,
      affected_products: [],
    };

    tierModal.open();
  }

  function parser(v) {
    if (v === "" || v === undefined || v === null) return null;
    if (typeof v !== "string") throw new Error("Invalid numeric value");

    const num = v.match(/\d+(\.\d*)?|\.\d+/);
    if (!num) throw new Error("Invalid numeric value");

    const d = parseFloat(num[0]);
    if (!Number.isFinite(d)) {
      throw new Error("Invalid numeric value");
    }

    if (d < 0) {
      throw new Error("Value must be non-negative");
    }

    return d;
  }

  function formatter(v) {
    if (v === "Mixed") return "Mixed";
    if (!Number.isFinite(v)) return "";
    return v.toFixed(2);
  }

  function addTier() {
    const tier = product.data.tiers || [];
    tier.push(newTier);
    dispatch("update", { id: product.id, prop: "data.tiers", value: tier });
  }

  function deleteTier(index) {
    const tiers = product.data.tiers;
    tiers.splice(index, 1);
    dispatch("update", { id: product.id, prop: "data.tiers", value: tiers });
  }
</script>

<div class="text-xs px-4">
  {#if !multiple}
    <ImageInput
      label="Image"
      labelWidth="5.5rem"
      value={imageUrl(product.image)}
      accept="image/png, image/svg+xml"
      uploadType="arraybuffer"
      {disabled}
      on:upload={uploadImage}
      on:delete={() => update("image", null)} />
  {/if}
  <ContenteditableInput
    label="Name"
    {disabled}
    labelWidth="5.5rem"
    value={product.name}
    on:input={(e) => update("name", e.detail.value)} />
  <ContenteditableInput
    label="Description"
    {disabled}
    labelWidth="5.5rem"
    value={product.description}
    on:input={(evt) => update("description", evt.detail.value)} />
  <TextInput
    label="Classification"
    {disabled}
    labelWidth="5.5rem"
    value={product.classification}
    on:input={(e) => update("classification", e.detail.value)} />
  <BooleanInput
    label="Published"
    {disabled}
    labelWidth="5.5rem"
    value={product.published}
    on:input={(evt) => update("published", evt.detail.value)} />
  {#if $profile?.user_role === "developer" && !multiple}
    <CopyTextField label="ID" labelWidth="5.5rem" value={product.id} />
  {/if}
  <MultiSelectInput
    label="Affects"
    {disabled}
    labelWidth="5.5rem"
    value={product.data.affected_products || []}
    options={itemProducts.map((ip) => ({ label: ip.name, value: ip.id }))}
    on:input={(e) => update("data.affected_products", e.detail.value)} />
  {#if product.data.tiers}
    {@const affectedObj = product.data.affected_products.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {})}
    {@const affectedProds = itemProducts.filter((ip) => affectedObj[ip.id])}
    <div class="overflow-hidden">
      {#each product.data.tiers as tier, index}
        <div class="flex gap-1 items-center overflow-hidden">
          <div class="grow overflow-hidden">
            <MultiSelectInput
              label={`Tier: ${tier.multiplier}`}
              {disabled}
              labelWidth="5.5rem"
              value={tier.affected_products || []}
              options={affectedProds.map((ip) => ({ label: ip.name, value: ip.id }))}
              on:input={(e) => update(`data.tiers.${index}.affected_products`, e.detail.value)} />
          </div>
          {#if !disabled}
            <div class="flex-none">
              <button class="p-1 rounded hover:bg-gray-200" on:click={() => deleteTier(index)}>
                <XIcon />
              </button>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
  {#if !disabled}
    <div class="flex justify-end">
      <button on:click={beginAddingTier} class="p-1 rounded hover:bg-gray-200">
        <PlusIcon />
      </button>
    </div>
  {/if}
</div>

<Modal
  closeable
  on:confirm={() => addTier()}
  bind:this={tierModal}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Add Tier", type: "confirm", style: "primary" },
  ]}>
  <div slot="title">Add Tier</div>
  <div slot="content">
    <TextInput label="Multiplier" bind:value={newTier.multiplier} border {formatter} {parser} />
  </div>
</Modal>
